import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Shield, Fingerprint, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SecurityPostureProps {
  data: {
    threatLevel: string
    riskScore: number
    vulnerabilities: { critical: number; high: number; medium: number }
  }
}

export function SecurityPosture({ data }: SecurityPostureProps) {
  return (
    <Card className="p-6 bg-slate-900/50 border-slate-800 backdrop-blur-md relative overflow-hidden group">
      <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
        <Shield className="size-32" />
      </div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Lock className="size-4 text-blue-500" /> Security Posture
          </h3>
          <p className="text-xs text-slate-500 mt-1">Threat Intelligence Feed</p>
        </div>
        <Badge className={cn(
          "uppercase text-[10px] font-bold tracking-widest px-3 py-1 border-0",
          data.threatLevel === 'low' ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"
        )}>
          {data.threatLevel} threat
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div className="flex flex-col items-center justify-center p-6 bg-slate-950/50 rounded-2xl border border-slate-800">
          <span className="text-4xl font-black text-white font-mono">{data.riskScore}</span>
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">Risk Score</span>
        </div>
        <div className="space-y-4">
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-500 font-bold uppercase tracking-tighter">Critical CVEs</span>
            <span className="font-mono text-rose-500 font-bold">{data.vulnerabilities.critical}</span>
          </div>
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-500 font-bold uppercase tracking-tighter">High Risk</span>
            <span className="font-mono text-amber-500 font-bold">{data.vulnerabilities.high}</span>
          </div>
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-500 font-bold uppercase tracking-tighter">Medium</span>
            <span className="font-mono text-blue-500 font-bold">{data.vulnerabilities.medium}</span>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3 p-3 bg-slate-800/30 rounded-lg border border-slate-700/50">
          <Fingerprint className="size-4 text-blue-400" />
          <div className="flex-1">
            <p className="text-[10px] font-bold text-white uppercase tracking-tighter">Threat Intelligence</p>
            <p className="text-[10px] text-slate-400">No active threats detected</p>
          </div>
        </div>
      </div>
    </Card>
  )
}
